import { Link, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { css, styled as muiStyled } from '@mui/material/styles';
import React from 'react';

import { useDashboardDataContext } from './dashboard-context';

import { useDesignTokens } from '~/client/hooks/use-design-tokens';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';
import {
  governanceTabFromLabels,
  slotCategoryForTab,
  SLOT_CATEGORY_LABEL,
  type ReviewSlotCategory,
} from '~/shared/utils/assessment-category';
import Arranger from '~c/components/base/arranger';
import Button from '~c/components/base/button';
import { Drawer } from '~c/components/base/drawer';
import { useSnackbarContext } from '~c/components/base/snackbar';
import { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import TextField from '~c/components/base/text-field';

interface Props {
  open: boolean;
  onClose: () => void;
  className?: string;
  /** Whether the current user can manage assessments (review/approval stages). */
  canManageAssessments: boolean;
}

/** The assessment shape provided by the dashboard project context. */
type AssessmentItem = NonNullable<GQL.ProjectFragment['assessments']>[number];

/**
 * Each stage: the verb + an eligibility predicate over the assessment status.
 * Review/Approval follow the workflow state machine; Audit is independent
 * (any started, non-terminal assessment).
 */
const STAGE = {
  [GQL.AssignmentType.Review]: {
    type: GQL.AssignmentType.Review,
    verb: 'review',
    slotSuffix: 'REVIEWER',
    roleWord: 'Reviewer',
    isEligible: (s: GQL.AssessmentStatusType) =>
      s === GQL.AssessmentStatusType.InProgress,
  },
  [GQL.AssignmentType.Approval]: {
    type: GQL.AssignmentType.Approval,
    verb: 'approval',
    slotSuffix: 'APPROVER',
    roleWord: 'Approver',
    isEligible: (s: GQL.AssessmentStatusType) =>
      s === GQL.AssessmentStatusType.Reviewed,
  },
  [GQL.AssignmentType.Audit]: {
    type: GQL.AssignmentType.Audit,
    verb: 'audit',
    slotSuffix: 'REVIEWER',
    roleWord: 'Auditor',
    isEligible: (s: GQL.AssessmentStatusType) =>
      s !== GQL.AssessmentStatusType.NotStarted,
  },
} as const;

type StageType =
  | GQL.AssignmentType.Review
  | GQL.AssignmentType.Approval
  | GQL.AssignmentType.Audit;

const REFETCH = ['ProjectForDashboard'];

/** Due date default: today + 7 days, as yyyy-mm-dd for the date input. */
const defaultDueDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
};

const humanizeStatus = (s: string) =>
  s
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** One row of the "who gets what" plan shown before assigning. */
interface PlanRow {
  label: string;
  assigneeText: string;
  ok: boolean;
}

/**
 * Bulk-assign every ready assessment in a project for review, approval or
 * audit in one click. Reviewers/approvers/auditors come from Organization
 * Settings (the org project-role slots) — the drawer shows who each type will
 * go to, then dispatches. Assessments in the wrong workflow state are skipped.
 */
const BulkAssignDrawerImpl: React.FC<Props> = ({
  open,
  onClose,
  className,
  canManageAssessments,
}) => {
  const Snackbar = useSnackbarContext();
  const tokens = useDesignTokens();
  const { project } = useDashboardDataContext();
  const { userProfile } = useUserProfile();

  // Only project Auditors may bulk-assign audits (enforced server-side too), so
  // the Audit stage is hidden from everyone else. Review/Approval are for
  // assessment managers.
  const isProjectAuditor = React.useMemo(
    () =>
      (project?.sharedWith ?? []).some(
        (m) =>
          m?.email?.toLowerCase() === userProfile?.email?.toLowerCase() &&
          (m?.role ?? '').toLowerCase() === 'auditor'
      ),
    [project?.sharedWith, userProfile?.email]
  );

  const [stageType, setStageType] = React.useState<StageType>(
    canManageAssessments ? GQL.AssignmentType.Review : GQL.AssignmentType.Audit
  );
  const [dueDate, setDueDate] = React.useState(defaultDueDate());

  const [bulkAssign, { loading }] = GQL.useBulkAssignAssessmentActionMutation({
    refetchQueries: REFETCH,
  });

  // Org role-slot people for this project (who reviews/approves/audits).
  const { data: slotData } = GQL.useProjectRoleSlotAssignmentsQuery({
    variables: { projectId: project?._id ?? '' },
    skip: !project?._id,
    fetchPolicy: 'cache-and-network',
  });

  const stage = STAGE[stageType];
  const isAudit = stage.type === GQL.AssignmentType.Audit;
  // Where an admin configures the org's reviewer/approver/auditor slots.
  const orgSettingsUrl = `/org-admin/${project?.orgId ?? ''}/dashboard?tab=2`;

  // Refresh the due date each time the drawer opens.
  React.useEffect(() => {
    if (open) {
      setDueDate(defaultDueDate());
    }
  }, [open]);

  // slot → { email, name }
  const slotPerson = React.useMemo(() => {
    const map = new Map<string, { email: string; name: string }>();
    for (const a of slotData?.projectRoleSlotAssignments ?? []) {
      const email = (a.userEmail ?? '').toLowerCase();
      const name =
        `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || a.userEmail;
      map.set(a.slot, { email, name });
    }
    return map;
  }, [slotData]);

  const assessments = React.useMemo(
    () =>
      (project?.assessments ?? []).filter(
        (a) =>
          a.status !== GQL.AssessmentStatusType.Archived &&
          a.status !== GQL.AssessmentStatusType.Cancelled
      ),
    [project?.assessments]
  );

  // Eligible (ready to send for this stage) vs ineligible (wrong state).
  const { eligible, ineligible } = React.useMemo(() => {
    const e: AssessmentItem[] = [];
    const i: AssessmentItem[] = [];
    for (const a of assessments) {
      (stage.isEligible(a.status) ? e : i).push(a);
    }
    return { eligible: e, ineligible: i };
  }, [assessments, stage]);

  // Auditors = people in the org AUDIT_REVIEWER + AUDIT_APPROVER slots (deduped).
  const auditors = React.useMemo(() => {
    if (!isAudit) {
      return [];
    }
    const seen = new Set<string>();
    const list: { email: string; name: string }[] = [];
    for (const slot of ['AUDIT_REVIEWER', 'AUDIT_APPROVER']) {
      const p = slotPerson.get(slot);
      if (p && !seen.has(p.email)) {
        seen.add(p.email);
        list.push(p);
      }
    }
    return list;
  }, [isAudit, slotPerson]);

  // The "who gets what" plan. A configured slot is enough — the server adds
  // the routed person to the project if they aren't a stakeholder yet.
  const { plan, assignableCount } = React.useMemo(() => {
    if (isAudit) {
      const ok = auditors.length > 0;
      const rows: PlanRow[] =
        eligible.length > 0
          ? [
              {
                label: 'Auditor(s)',
                assigneeText: ok
                  ? auditors.map((m) => m.name).join(', ')
                  : 'Not set in Organization Settings',
                ok,
              },
            ]
          : [];
      return { plan: rows, assignableCount: ok ? eligible.length : 0 };
    }

    const byCat = new Map<ReviewSlotCategory, number>();
    for (const a of eligible) {
      const cat = slotCategoryForTab(governanceTabFromLabels(a.policy?.labels));
      byCat.set(cat, (byCat.get(cat) ?? 0) + 1);
    }
    const rows: PlanRow[] = [];
    let count = 0;
    for (const [cat, n] of byCat) {
      const person = slotPerson.get(`${cat}_${stage.slotSuffix}`);
      const ok = !!person;
      if (ok) {
        count += n;
      }
      rows.push({
        label: `${SLOT_CATEGORY_LABEL[cat]} ${stage.roleWord}`,
        assigneeText: person ? person.name : 'Not set in Organization Settings',
        ok,
      });
    }
    return { plan: rows, assignableCount: count };
  }, [isAudit, auditors, eligible, slotPerson, stage]);

  const includedIds = React.useMemo(
    () => eligible.map((a) => a._id),
    [eligible]
  );

  // Group skipped (wrong-state) assessments by a readable status.
  const skippedByStatus = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const a of ineligible) {
      const k = humanizeStatus(a.status);
      map.set(k, (map.get(k) ?? 0) + 1);
    }
    return Array.from(map.entries());
  }, [ineligible]);

  const handleSubmit = async () => {
    if (!project?._id) {
      return;
    }
    if (!dueDate) {
      Snackbar.open('Select a due date');
      return;
    }
    if (includedIds.length === 0) {
      Snackbar.open(`No assessments are ready for ${stage.verb}`);
      return;
    }
    try {
      const res = await bulkAssign({
        variables: {
          input: {
            projectId: project._id,
            assessmentIds: includedIds,
            dueDate: new Date(dueDate).getTime(),
            type: stage.type,
            sendNotification: true,
          },
        },
      });
      const result = res.data?.bulkAssignAssessmentAction;
      const done = result?.assignedCount ?? 0;
      const skippedCount = result?.skipped?.length ?? 0;
      Snackbar.open(
        skippedCount > 0
          ? `Assigned ${done} for ${stage.verb}; ${skippedCount} skipped`
          : `Assigned ${done} assessment(s) for ${stage.verb}`,
        { showOk: true }
      );
      onClose();
    } catch (e) {
      Snackbar.open(
        e instanceof Error ? e.message : 'Failed to assign assessments'
      );
    }
  };

  const actionButtons = (
    <Button
      color="primary"
      size="small"
      onClick={handleSubmit}
      loading={loading}
      disabled={loading || assignableCount === 0}
    >
      {assignableCount > 0
        ? `Assign ${assignableCount} for ${stage.verb}`
        : 'Assign'}
    </Button>
  );

  const summaryTone =
    eligible.length > 0
      ? { bg: tokens.status.successBg, fg: tokens.status.success }
      : { bg: tokens.status.neutralBg, fg: tokens.status.neutral };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      width={680}
      title="Bulk assign"
      actionButtons={actionButtons}
    >
      <Arranger direction="column" className={className}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          color="primary"
          className="stage-toggle"
          value={stageType}
          onChange={(_e, val) => val && setStageType(val)}
        >
          {canManageAssessments && (
            <ToggleButton value={GQL.AssignmentType.Review}>
              Review
            </ToggleButton>
          )}
          {canManageAssessments && (
            <ToggleButton value={GQL.AssignmentType.Approval}>
              Approval
            </ToggleButton>
          )}
          {isProjectAuditor && (
            <ToggleButton value={GQL.AssignmentType.Audit}>Audit</ToggleButton>
          )}
        </ToggleButtonGroup>

        <div
          className="banner"
          style={{ backgroundColor: summaryTone.bg, color: summaryTone.fg }}
        >
          <Text sz="bold-body14" color="inherit">
            {eligible.length} of {assessments.length} ready for {stage.verb}
          </Text>
        </div>

        {/* Who each type will be assigned to (from Organization Settings) */}
        {plan.length > 0 && (
          <div className="section">
            <Text
              sz="bold-body12"
              className="section-title"
              color="text.secondary"
            >
              Will be assigned to
            </Text>
            {plan.map((row) => (
              <Text key={row.label} sz="body14" className="plan-row">
                {row.label}
                {' → '}
                {row.ok ? (
                  <Text component="span" sz="body14" color="text.secondary">
                    {row.assigneeText}
                  </Text>
                ) : (
                  <Link
                    href={orgSettingsUrl}
                    underline="hover"
                    sx={{ fontSize: '0.875rem', color: tokens.status.warning }}
                  >
                    Set in Organization Settings
                  </Link>
                )}
              </Text>
            ))}
          </div>
        )}

        <TextField
          type="date"
          label="Due date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
          fullWidth
        />

        {/* Not ready (wrong workflow state) — secondary */}
        {skippedByStatus.length > 0 && (
          <div className="section">
            <Text
              sz="bold-body12"
              className="section-title"
              color="text.secondary"
            >
              Not ready · {ineligible.length}
            </Text>
            {skippedByStatus.map(([status, n]) => (
              <Text
                key={status}
                sz="body14"
                color="text.secondary"
                className="plan-row"
              >
                {status} · {n}
              </Text>
            ))}
          </div>
        )}

        <Text sz="body12" color="text.secondary" className="footnote">
          {isAudit
            ? 'Audits go to your organization’s auditor(s). '
            : 'Each assessment goes to your organization’s reviewer/approver for its type — set in Organization Settings. '}
          If they aren’t a project member yet, they’re added automatically.
          Types with no one set are skipped. Each person gets one notification
          summarising their tasks.
        </Text>
      </Arranger>
    </Drawer>
  );
};

const BulkAssignDrawer = muiStyled(BulkAssignDrawerImpl)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: left;
    gap: ${theme.spacing(spc.s)};

    .stage-toggle {
      width: 100%;
    }
    .stage-toggle .MuiToggleButton-root {
      flex: 1;
      text-transform: none;
      font-weight: 600;
    }

    .banner {
      display: flex;
      align-items: center;
      padding: ${theme.spacing(spc.xs)};
      border-radius: ${theme.shape.borderRadius}px;
    }

    .section {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(spc.xxs)};
    }

    .section-title {
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .plan-row {
      display: block;
      padding: ${theme.spacing(spc.xxs)} 0;
    }

    .footnote {
      display: block;
    }

    .MuiFormControl-root {
      width: 100%;
    }
  `
);

export default BulkAssignDrawer;
